import { AnchorItem } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/types';
import { useEffect, useRef } from 'react';
import { DEFAULT_ANCHOR } from './anchorData';
import { MANUAL_SCROLL_THRESHOLD } from './constants';
import { getAllAnchorHrefs } from './utils';

export const useScrollDetection = (
  contentWrapperRef: React.RefObject<HTMLDivElement>,
  isClickingRef: React.MutableRefObject<boolean>,
  isInitializingRef: React.MutableRefObject<boolean>,
  setCurrentAnchor: (anchor: string) => void,
  anchorItems: AnchorItem[],
  targetScrollRef?: React.MutableRefObject<number | null>,
  lastAnchorRef?: React.MutableRefObject<string | null>
) => {
  // Cache for performance (scroll position only, anchor state is shared via lastAnchorRef)
  const cacheRef = useRef<{
    rafId: number | null;
    lastScrollTop: number | null;
  }>({
    rafId: null,
    lastScrollTop: null,
  });

  // Use ref to always get latest anchorItems (avoids stale closure in scroll listener)
  const anchorItemsRef = useRef(anchorItems);
  anchorItemsRef.current = anchorItems;

  // Detect scroll and update current anchor
  useEffect(() => {
    // Wait a bit for initialization to complete before setting up scroll detection
    const setupScrollDetection = () => {
      const scrollContainer =
        contentWrapperRef.current?.closest('.ant-drawer-body');
      if (!scrollContainer || !(scrollContainer instanceof HTMLElement)) {
        // Retry if drawer not ready yet
        setTimeout(setupScrollDetection, 50);
        return;
      }

      // Wait for initialization to complete
      if (isInitializingRef.current) {
        setTimeout(setupScrollDetection, 100);
        return;
      }

      /**
       * Detect if user is manually scrolling in opposite direction of smooth scroll.
       * This allows us to cancel the "clicking" state immediately when user
       * interrupts the smooth scroll animation.
       */
      const isManualScrollDetected = (currentScrollTop: number): boolean => {
        // Only check during click-initiated scroll
        if (!isClickingRef.current || !targetScrollRef?.current) {
          return false;
        }

        const lastScrollTop = cacheRef.current.lastScrollTop;
        if (lastScrollTop === null) {
          return false;
        }

        const targetScroll = targetScrollRef.current;
        const scrollDelta = currentScrollTop - lastScrollTop;

        // Determine scroll directions
        const isScrollingDown = scrollDelta > 0;
        const shouldScrollDown = targetScroll > lastScrollTop;

        // If scrolling direction is opposite to what smooth scroll should do
        // and the scroll is significant (not just minor fluctuation)
        const isOppositeDirection = isScrollingDown !== shouldScrollDown;
        const isSignificantScroll =
          Math.abs(scrollDelta) > MANUAL_SCROLL_THRESHOLD;

        return isOppositeDirection && isSignificantScroll;
      };

      /**
       * Cancel clicking state when manual scroll is detected.
       * This immediately re-enables normal scroll detection.
       */
      const handleManualScrollDetection = (currentScrollTop: number) => {
        if (isManualScrollDetected(currentScrollTop)) {
          isClickingRef.current = false;
          if (targetScrollRef) {
            targetScrollRef.current = null;
          }
        }
      };

      /**
       * Process scroll position and update active anchor.
       * Uses equal division algorithm to determine which section is active.
       */
      const processScroll = () => {
        cacheRef.current.rafId = null;

        if (isInitializingRef.current || !contentWrapperRef.current) {
          return;
        }

        const currentScrollTop = scrollContainer.scrollTop;

        // Check for manual scroll and cancel clicking state if detected
        handleManualScrollDetection(currentScrollTop);

        // Update last scroll position for next comparison
        cacheRef.current.lastScrollTop = currentScrollTop;

        // Skip anchor updates during click-initiated scroll
        if (isClickingRef.current) {
          return;
        }

        // Use ref to get latest anchorItems (avoids stale closure)
        const currentAnchorItems = anchorItemsRef.current;
        const allHrefs = getAllAnchorHrefs(currentAnchorItems);
        if (allHrefs.length === 0) {
          return;
        }

        // Calculate total scrollable height
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;
        const totalScrollableHeight = scrollHeight - clientHeight;

        // If no scrollable content, use first anchor
        if (totalScrollableHeight <= 0) {
          const activeAnchor = allHrefs[0] ?? DEFAULT_ANCHOR;
          if (lastAnchorRef?.current !== activeAnchor) {
            if (lastAnchorRef) {
              lastAnchorRef.current = activeAnchor;
            }
            setCurrentAnchor(activeAnchor);
          }
          return;
        }

        // Divide scroll height equally among anchors
        const sectionHeight = totalScrollableHeight / allHrefs.length;

        // Determine which section the current scroll position falls into
        const activeIndex = Math.min(
          Math.floor(currentScrollTop / sectionHeight),
          allHrefs.length - 1
        );

        const activeAnchor = allHrefs[activeIndex] ?? DEFAULT_ANCHOR;

        // Only update if anchor changed (use shared lastAnchorRef for sync with click handler)
        if (lastAnchorRef?.current !== activeAnchor) {
          if (lastAnchorRef) {
            lastAnchorRef.current = activeAnchor;
          }
          setCurrentAnchor(activeAnchor);
        }
      };

      /**
       * Throttle scroll events using requestAnimationFrame.
       * Prevents performance issues on Safari and other browsers.
       */
      const handleScroll = () => {
        if (isInitializingRef.current || !contentWrapperRef.current) {
          return;
        }

        // Throttle using RAF - only schedule if not already scheduled
        if (cacheRef.current.rafId === null) {
          cacheRef.current.rafId = requestAnimationFrame(processScroll);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });

      return () => {
        if (cacheRef.current.rafId !== null) {
          cancelAnimationFrame(cacheRef.current.rafId);
          cacheRef.current.rafId = null;
        }
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    };

    // Start setup after a short delay to let initialization run first
    const timeoutId = setTimeout(setupScrollDetection, 50);

    return () => {
      clearTimeout(timeoutId);
      if (cacheRef.current.rafId !== null) {
        cancelAnimationFrame(cacheRef.current.rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        cacheRef.current.rafId = null;
      }
    };
  }, [
    contentWrapperRef,
    anchorItems,
    isInitializingRef,
    isClickingRef,
    setCurrentAnchor,
    targetScrollRef,
    lastAnchorRef,
  ]);
};
